import { BlueText } from "components/common";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import ChartByTime, { TimeRangeConfig } from "components/dashboard/ChartByTime";
import { UnknownDevice } from "constant";
import Decimal from "decimal.js";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { ChartAttribute } from "generated/models";
import useChartWidth from "hooks/useChartWidth";
import { cloneDeep, get, isEmpty, set } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import dashboardService from "service/dashboardService";
import LocalStorageService from "service/localStorageService";
import { getConsumptionMetricV3 } from "service/proqueryService";
import styledConst from "styles";
import { ConsumptionChartBy } from "types";
import { BarChartSeries, ChartState } from "types/Charts";
import { SensorDetails } from "types/Sensors";
import { getAPIError, showErrorAlert } from "utils/alert";
import {
    addDecimalPlace,
    convertPrometheusTimeToSeconds,
    formatValueWithUnit,
    formatNullableNumber,
    isHttpSuccess,
    parseColorToHex,
} from "utils/functions";
import WidgetBox from "../WidgetBox";
import {
    getConsumptionCLSFromMetricName,
    getSensorType,
} from "utils/sensorFunctions";
import { SensorType } from "constant/gateway";
import { dateTimeFormatter } from "utils/dateTimeFormatter";
import {
    createBoundedTooltipPositioner,
    getInvisibleSuffix,
} from "utils/chart";
import styled from "@emotion/styled";

const DEFAULT_ACCURACY: string = "4";
const PERCENTAGE_ACCURACY: number = 1;

type SensorConfig = Record<string, any> &
    Pick<ChartAttribute, "device_id" | "said"> & {
        sensor_name: string;
    };

const generateSensorDetails = (
    attributes: ChartAttribute[],
    sensorConfigs: SensorConfig[]
): SensorDetails[] => {
    return attributes.map((attribute, index) => {
        const { device_id, said } = attribute;

        const sensor = sensorConfigs.find(
            ({ device_id: sensorId, said: sensorSaid }) =>
                device_id === sensorId && said === sensorSaid
        );

        const sensorName = sensor?.sensor_name ?? UnknownDevice;
        return {
            ...attribute,
            name: `${sensorName}${getInvisibleSuffix(index)}`,
            color: parseColorToHex(attribute.color ?? ""),
        };
    });
};

/**
 * Calculates and formats the percentage increase / decrease text.
 * @param currentTotal Total value for the current period.
 * @param previous Total value for the previous period.
 * @param period Time period (e.g. "day", "week" etc.) to be displayed.
 * @returns Returns the formatted string that can be used directly in the chart.
 */
const calculatePercentage = (
    totalValue: number | null,
    previousValue: number | null,
    period: string
): string => {
    // Condition from this ticket => https://brtchip.atlassian.net/browse/IOTP-7133

    // Case 2: If there is no metric for any one of the periods, don't show percentage
    if (previousValue === null || totalValue === null) {
        return "";
    }

    // Assuming calculations before this point to be accurate even when using IEEE 754 numbers
    // Converting to Decimal to handle edge cases (e.g. previous = 100, current = 105.05)
    const previous = new Decimal(previousValue);
    const current = new Decimal(totalValue);

    // Case 3: If current equals previous, don't show percentage
    if (previous.cmp(current) === 0) {
        return "";
    }

    let percentage: Decimal;
    // Case 1a & 1b: Handle zero cases
    if (previous.cmp(0) === 0 || current.cmp(0) === 0) {
        // If previous is 0 and current is not 0 -> 100% increase
        // If current is 0 and previous is not 0 -> 100% decrease
        percentage = new Decimal(100);
    } else {
        // Normal case: calculate percentage difference
        // Using the original values without limiting decimal places for calculation
        percentage = current.sub(previous).div(previous.abs()).mul(100).abs();
    }

    // Round to specified accuracy only for display
    const roundedPercentage = percentage.toDP(
        PERCENTAGE_ACCURACY,
        Decimal.ROUND_HALF_UP
    );

    // Don't show if rounded percentage is 0
    if (roundedPercentage.cmp(0) === 0) {
        return "";
    }

    // Determine if it's an increase or decrease
    const isIncrease = current.cmp(previous) > 0;
    const arrow = isIncrease ? "{s5| ↑}" : "{s6| ↓}";
    const percentageStr = roundedPercentage.toFixed(
        PERCENTAGE_ACCURACY,
        Decimal.ROUND_HALF_UP
    );

    const change = isIncrease ? "increase" : "decrease";
    return `${arrow} {s3| ${percentageStr}%} {s1|${change} from previous ${period}}`;
};

/**
 * Calculates the average value over time slots that have data.
 * @param total Pre-computed sum of all series values (from the service).
 * @param series Series data used to count how many time slots have valid values.
 * @returns Returns an object containing the average value.
 */
export const getAverageData = (
    total: number | null,
    series: BarChartSeries[]
): { average: number | null } => {
    if (total === null) {
        return { average: null };
    }

    const numberOfDates = Math.max(
        ...series.map((s) => s?.data?.length || 0),
        0
    );
    const datesWithData = new Set<number>();

    for (let dateIndex = 0; dateIndex < numberOfDates; dateIndex++) {
        for (const element of series) {
            const value = element.data[dateIndex];
            if (typeof value === "number" && !Number.isNaN(value)) {
                datesWithData.add(dateIndex);
                break;
            }
        }
    }

    return {
        average: datesWithData.size > 0 ? total / datesWithData.size : null,
    };
};

const detectGroupBy = (timeRange: string) => {
    const duration = convertPrometheusTimeToSeconds(timeRange);
    const units = { y: 31536000, w: 604800, d: 86400, h: 3600, m: 60, s: 1 };
    let groupBy = ConsumptionChartBy.Day;

    if (units.d < duration && duration <= units.w) {
        groupBy = ConsumptionChartBy.Week;
    } else if (units.d * 28 <= duration && duration <= units.d * 31) {
        groupBy = ConsumptionChartBy.Month;
    } else if (units.y <= duration) {
        groupBy = ConsumptionChartBy.Year;
    }

    return groupBy;
};

const getDefaultSensorName = (sensorCls: string) => {
    const sensorType = getSensorType({ CLS: sensorCls });
    return get(ChartType, sensorType ?? "", "");
};

const getUnit = (sensorCls: string) => {
    const sensorType = getSensorType({ CLS: sensorCls });
    return get(ChartUnit, sensorType ?? "", "");
};

const getColor = (sensorCls: string) => {
    const sensorType = getSensorType({ CLS: sensorCls });
    return get(ChartColor, sensorType ?? "", "");
};

const getCurrentLastTime = (groupBy: ConsumptionChartBy) => {
    const defaultTime = {
        [ConsumptionChartBy.Day]: moment().endOf("days"),
        [ConsumptionChartBy.Week]: moment().endOf("weeks"),
        [ConsumptionChartBy.Month]: moment().endOf("months"),
        [ConsumptionChartBy.Year]: moment().endOf("years"),
    };
    return defaultTime[groupBy];
};

const getTimeRange = (groupBy: ConsumptionChartBy) => {
    const defaultTime = {
        [ConsumptionChartBy.Day]: "1d",
        [ConsumptionChartBy.Week]: "7d",
        [ConsumptionChartBy.Month]: "30d",
        [ConsumptionChartBy.Year]: "365d",
    };
    return defaultTime[groupBy];
};

const ChartType = {
    [SensorType.WaterConsumption]: "Water Consumption",
    [SensorType.EnergyConsumption]: "Energy Consumption",
};

const ChartUnit = {
    [SensorType.WaterConsumption]: "m³",
    [SensorType.EnergyConsumption]: "kWh",
};

const ChartColor = {
    [SensorType.WaterConsumption]: "#4d9eea",
    [SensorType.EnergyConsumption]: "#5ebfc6",
};

const AverageText = {
    [ConsumptionChartBy.Day]: "Hourly",
    [ConsumptionChartBy.Week]: "Daily",
    [ConsumptionChartBy.Month]: "Daily",
    [ConsumptionChartBy.Year]: "Monthly",
};

const ConsumptionChartOptions = {
    responsive: true,
    backgroundColor: "#2F3A51",
    title: {
        show: true,
        left: 14,
        top: 15,
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
            overflow: "truncate",
            ellipsis: "...",
        },
        subtextStyle: {
            color: styledConst.Primary_Blue_4,
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: 14,
            overflow: "truncate",
            ellipsis: "...",
            rich: {
                s1: {
                    color: styledConst.Primary_Blue_4,
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 14,
                    height: 25,
                },
                s2: {
                    fontSize: 22,
                    fontWeight: "bold",
                    color: "#5ebfc6",
                },
                s3: {
                    color: "white",
                    fontSize: 14,
                    fontFamily: "Roboto",
                },
                s4: {
                    color: styledConst.Primary_Blue_4,
                    fontSize: 6,
                },
                s5: {
                    color: "#F2495E",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 16,
                },
                s6: {
                    color: "#00CBAF",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 16,
                },
            },
        },
    },
    legend: {
        type: "scroll",
        selectedMode: true,
        left: 15,
        bottom: 40,
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
        },
        icon: "circle",
        pageTextStyle: {
            color: "#ffffff",
        },
        pageIconInactiveColor: "#6c6d6e",
        pageIconColor: "#c8c6c6",
    },
    tooltip: {
        trigger: "axis",
        confine: true,
        renderMode: "html",
        appendTo: "body",
        position: createBoundedTooltipPositioner(),
    },
    toolbox: {
        feature: {
            saveAsImage: {
                icon: "path://M8.79999 3.71991C7.92975 3.71991 7.07906 3.97797 6.35548 4.46144C5.63191 4.94492 5.06795 5.63211 4.73493 6.4361C4.4019 7.2401 4.31477 8.12479 4.48454 8.97831C4.65432 9.83182 5.07337 10.6158 5.68872 11.2312C6.30407 11.8465 7.08808 12.2656 7.94159 12.4354C8.79511 12.6051 9.6798 12.518 10.4838 12.185C11.2878 11.852 11.975 11.288 12.4584 10.5644C12.9419 9.84084 13.2 8.99015 13.2 8.11991C13.2 6.95296 12.7364 5.8338 11.9113 5.00864C11.0861 4.18348 9.96694 3.71991 8.79999 3.71991ZM8.79999 11.6399C8.1038 11.6399 7.42324 11.4335 6.84438 11.0467C6.26552 10.6599 5.81436 10.1101 5.54794 9.46695C5.28152 8.82376 5.21181 8.116 5.34763 7.43319C5.48345 6.75038 5.8187 6.12317 6.31098 5.63089C6.80326 5.13861 7.43046 4.80336 8.11327 4.66754C8.79608 4.53172 9.50384 4.60143 10.147 4.86785C10.7902 5.13427 11.34 5.58544 11.7268 6.1643C12.1135 6.74316 12.32 7.42372 12.32 8.11991C12.32 9.05347 11.9491 9.9488 11.289 10.6089C10.6289 11.2691 9.73355 11.6399 8.79999 11.6399Z M14.52 1.95998H12.5928L12.2276 1.22958C12.0602 0.930245 11.8192 0.678578 11.5274 0.498406C11.2356 0.318234 10.9026 0.215518 10.56 0.199982H7.04C6.69739 0.215518 6.36442 0.318234 6.0726 0.498406C5.78079 0.678578 5.53978 0.930245 5.3724 1.22958L5.0072 1.95998H3.08C2.26313 1.95998 1.47972 2.28448 0.902111 2.86209C0.324499 3.4397 0 4.22311 0 5.03998V12.08C0 12.8968 0.324499 13.6803 0.902111 14.2579C1.47972 14.8355 2.26313 15.16 3.08 15.16H14.52C15.3369 15.16 16.1203 14.8355 16.6979 14.2579C17.2755 13.6803 17.6 12.8968 17.6 12.08V5.03998C17.6 4.22311 17.2755 3.4397 16.6979 2.86209C16.1203 2.28448 15.3369 1.95998 14.52 1.95998ZM16.72 12.08C16.72 12.6635 16.4882 13.223 16.0756 13.6356C15.663 14.0482 15.1035 14.28 14.52 14.28H3.08C2.49652 14.28 1.93694 14.0482 1.52436 13.6356C1.11178 13.223 0.88 12.6635 0.88 12.08V5.03998C0.88 4.4565 1.11178 3.89693 1.52436 3.48435C1.93694 3.07177 2.49652 2.83998 3.08 2.83998H5.28C5.36125 2.83973 5.44085 2.81699 5.50997 2.77428C5.57909 2.73156 5.63503 2.67054 5.6716 2.59798L6.16 1.62558C6.25235 1.47089 6.38047 1.34061 6.53359 1.24568C6.68671 1.15074 6.86037 1.09392 7.04 1.07998H10.56C10.7396 1.09392 10.9133 1.15074 11.0664 1.24568C11.2195 1.34061 11.3476 1.47089 11.44 1.62558L11.9284 2.59798C11.965 2.67054 12.0209 2.73156 12.09 2.77428C12.1591 2.81699 12.2387 2.83973 12.32 2.83998H14.52C15.1035 2.83998 15.663 3.07177 16.0756 3.48435C16.4882 3.89693 16.72 4.4565 16.72 5.03998V12.08Z",
            },
        },
        top: 14,
        right: 48,
        emphasis: {
            iconStyle: {
                textPosition: "top",
                textPadding: -4,
            },
        },
        iconStyle: {
            borderColor: "#00CBAF",
        },
    },
    grid: {
        top: 135,
        left: "3%",
        right: "4%",
        bottom: 80,
        containLabel: true,
    },
    xAxis: {
        type: "category",
        axisTick: {
            alignWithLabel: true,
        },
    },
    yAxis: {
        type: "value",
    },
};

type ConsumptionChartProps = Readonly<
    Record<string, any> & {
        panel: Record<string, any> & {
            query_time: number | null;
            time_range: string;
            attributes: ChartAttribute[];
            uuid: string;
            name: string;
        };
    }
>;

// Extract tooltip formatter function to reduce nesting
const createTooltipFormatter = (
    getConsumptionTimeFormat: (name: string) => string,
    accuracy: string,
    unit: string
) => {
    return (params: any) => {
        const { name } = params[0];
        let series = ``;
        params.forEach(
            ({ seriesName, value, marker }: any) =>
                (series += `<span class="d-flex justify-content-between" >
                    <span>${marker}${seriesName}</span>
                    <span class="ml-2 font-weight-bold">${formatValueWithUnit(
                        formatNullableNumber(value, accuracy),
                        unit
                    )}</span>
                </span>
                `)
        );
        return `
                    ${getConsumptionTimeFormat(name)} <br />
                    ${series}
                `;
    };
};

// Extract Y-axis formatter function to reduce nesting
const createYAxisFormatter = (accuracy: string) => {
    return (value: string) => {
        return addDecimalPlace(value, accuracy);
    };
};

// Extract X-axis formatter function to reduce nesting
const createXAxisFormatter = (
    getConsumptionTimeFormat: (value: string, isLabel: boolean) => string
) => {
    return (value: string) => getConsumptionTimeFormat(value, true);
};

// Extract sensor config mapping logic to reduce nesting
const createSensorConfig = (
    attribute: ChartAttribute,
    gatewaysConfig: any,
    jsonList: any[]
): SensorConfig => {
    const {
        said = 0,
        device_id,
        gateway_id = "",
        time_series = "",
    } = attribute;

    const sensor = gatewaysConfig[gateway_id]?.find(
        ({ UID }: { UID: string }) => UID === device_id
    );

    if (sensor) {
        const { CLS, ACCURACY } =
            jsonList.find(({ OBJ }: { OBJ: string }) => OBJ === sensor?.OBJ)
                ?.SNS?.[said] ?? {};

        return {
            ...sensor,
            device_id,
            said,
            sensor_name: sensor?.sensor_names?.[said] ?? UnknownDevice,
            CLS: CLS,
            ACCURACY,
        };
    }

    // Sensor (LDSU) was deleted from the gateway so get from
    const sensorCls = getConsumptionCLSFromMetricName(time_series);

    return {
        ...sensor,
        device_id,
        said,
        sensor_name: sensor?.sensor_names?.[said] ?? UnknownDevice,
        CLS: sensorCls,
        ACCURACY: DEFAULT_ACCURACY,
    };
};

// Extract chart ready handler to reduce nesting
const createChartReadyHandler = (
    setSelectedLegend: (selected: Record<string, boolean>) => void
) => {
    return (chartInstance: any) => {
        chartInstance.on("legendselectchanged", function (changes: any) {
            setSelectedLegend(changes.selected);
        });
    };
};

const FilterWrapper = styled.div`
    position: absolute;
    right: 20px;
    top: 50px;
    z-index: 1;
`;

const ConsumptionChart = (props: ConsumptionChartProps) => {
    const jsonList = LocalStorageService.getItem("listOfJSON");
    // ----------------- props -----------------
    const {
        panel,
        isVisible,
        visibleRef,
        zoomLevel,
        dashboardId,
        gatewaysConfig,
    } = props;

    const {
        query_time = null,
        time_range = "",
        attributes = [],
        uuid: panelId = "",
        name = "",
    } = panel;

    const chartRef = useRef(null as any);

    // ----------------- chart -----------------
    const [chartState, setChartState] = useState<ChartState>({
        option: ConsumptionChartOptions,
        hasFetchedConfig: false,
        sensorConfigRes: undefined,
    });
    const [avg, setAvg] = useState("");
    const [groupBy, setGroupBy] = useState(ConsumptionChartBy.Day);
    const [timeRange, setTimeRange] = useState<TimeRangeConfig>({
        timeRange: "1d",
        lastTime: moment(),
    });
    const [selectedLegend, setSelectedLegend] = useState<
        Record<string, boolean>
    >({});

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 250,
    });

    const titleThreshold = 150;

    const chartInstance: any = chartRef.current?.getEchartsInstance();

    // ! ----------------- functions -----------------

    const getNewOptions = (baseOption: EChartsOption, newConfigArr: any[]) => {
        const newOption = cloneDeep(baseOption); // immutable
        newConfigArr.forEach(({ path, value }: any) =>
            set(newOption, path, value)
        );

        return newOption;
    };

    const getConsumptionTimeFormat = (value: string, isLabel = false) => {
        const lastTime = timeRange.lastTime;

        return dateTimeFormatter.chart(value, groupBy, isLabel, lastTime);
    };

    const fetchNewData = async () => {
        const sensorConfigs = get(chartState, "sensorConfigRes", []);
        const sensorDetails = generateSensorDetails(attributes, sensorConfigs);

        const { series, categories, total, previousTotal } =
            await getConsumptionMetricV3({
                sensors: sensorConfigs,
                sensorDetails,
                queryTime: timeRange.lastTime.unix(),
                groupBy,
                queries: panel.queries,
            });

        if (!Array.isArray(series)) {
            return;
        }

        const object = get(chartState, "sensorConfigRes[0].CLS");
        const accuracy = DEFAULT_ACCURACY; // fixed from new change request
        const unit = getUnit(object);

        const groupByText = groupBy.toLowerCase();

        const { average } = getAverageData(total, series);

        const percentage = calculatePercentage(
            total,
            previousTotal,
            groupByText
        );

        const _selectedLegend = isEmpty(selectedLegend)
            ? series.reduce((acc: any, { name }: { name: string }) => {
                  acc[name] = true; // default to true for all series
                  return acc;
              }, {})
            : selectedLegend;

        const _options = [
            {
                path: "xAxis.data",
                value: categories,
            },
            {
                path: "xAxis.axisLabel.formatter",
                value: createXAxisFormatter(getConsumptionTimeFormat),
            },
            {
                path: "yAxis.axisLabel.formatter",
                value: createYAxisFormatter(accuracy),
            },
            {
                path: "series",
                value: series,
            },
            {
                path: "legend.data",
                value: sensorDetails.map((entry) => entry.name),
            },
            {
                path: "legend.selectedMode",
                value: attributes.length !== 1,
            },
            {
                path: "legend.selected",
                value: _selectedLegend,
            },
            {
                path: "title.subtext",
                value: `{s1|${getDefaultSensorName(object)} (${unit}) }
{s2|${formatValueWithUnit(
                    formatNullableNumber(total, accuracy),
                    unit
                )}}{s1| (Total for the ${groupByText})}
${percentage}`,
            },
            {
                path: "title.text",
                value: name,
            },
            {
                path: "title.textStyle.width",
                value: boxWidth + titleThreshold,
            },
            {
                path: "title.subtextStyle.width",
                value: boxWidth,
            },
            {
                path: "tooltip.formatter",
                value: createTooltipFormatter(
                    getConsumptionTimeFormat,
                    accuracy,
                    unit
                ),
            },
            {
                path: "title.subtextStyle.rich.s2.color",
                value: getColor(object),
            },
        ];

        if (attributes.length === 1) {
            _options.push({
                path: "title.subtextStyle.rich.s2.color",
                value: series[0].color,
            });
        }

        const newOption: any = getNewOptions(chartState.option, _options);
        setAvg(
            formatValueWithUnit(formatNullableNumber(average, accuracy), unit)
        );
        setChartState((chartState) => ({
            ...chartState,
            option: newOption,
        }));
    };

    // ! ----------------- useEffect -----------------

    useEffect(() => {
        if (!isVisible || !chartState.hasFetchedConfig) return;
        if (chartState.hasFetchedConfig && isVisible) {
            fetchNewData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, chartState.hasFetchedConfig, timeRange]);

    useEffect(() => {
        const fetchSensorConfig = async () => {
            if (
                !isVisible ||
                chartState.hasFetchedConfig ||
                isEmpty(Object.values(gatewaysConfig))
            )
                return;

            const sensorConfigRes = attributes.map((attribute) =>
                createSensorConfig(attribute, gatewaysConfig, jsonList)
            );

            setChartState((chartState) => ({
                ...chartState,
                hasFetchedConfig: true,
                sensorConfigRes,
            }));
        };

        fetchSensorConfig();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartState, isVisible, gatewaysConfig]);

    useEffect(() => {
        const groupBy = detectGroupBy(time_range);
        const lastTime = query_time
            ? moment(query_time)
            : getCurrentLastTime(groupBy);

        setTimeRange({
            timeRange: time_range,
            lastTime: lastTime,
        });

        setGroupBy(groupBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelId]);

    useEffect(() => {
        chartInstance?.setOption({
            title: {
                textStyle: {
                    width: boxWidth + titleThreshold,
                },
                subtextStyle: {
                    width: boxWidth,
                },
            },
        });
    }, [chartInstance, boxWidth]);

    const onTimeChange = async (
        timeRange: TimeRangeConfig,
        groupBy: ConsumptionChartBy
    ) => {
        setTimeRange(timeRange);
        setGroupBy(groupBy);

        const panelRes = await dashboardService.updatePanel(
            dashboardId,
            panelId,
            {
                time_range: getTimeRange(groupBy),
            }
        );
        if (!isHttpSuccess(panelRes.status)) {
            showErrorAlert(
                getAPIError(
                    panelRes,
                    "Something went wrong when changing the time range. Please try again."
                )
            );
        }
    };

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget position-relative">
                <ContentWrapper isLoading={!chartState.hasFetchedConfig}>
                    <FilterWrapper>
                        <ChartByTime
                            timeRange={timeRange}
                            onChange={onTimeChange}
                            view={groupBy}
                        />
                    </FilterWrapper>
                    <div className="widget-tool">
                        <BlueText className="mr-1 refresh-drop font-weight-normal">
                            {AverageText[groupBy]} Avg:{" "}
                            <span className="text-white">{avg}</span>
                        </BlueText>
                    </div>
                    <ReactECharts
                        ref={chartRef}
                        className="chart-box"
                        theme={"dark"}
                        option={chartState.option}
                        notMerge={true}
                        lazyUpdate={true}
                        onChartReady={createChartReadyHandler(
                            setSelectedLegend
                        )}
                    />
                </ContentWrapper>
            </div>
        </WidgetBox>
    );
};

export default ConsumptionChart;
